import { createContext, useContext, useState, ReactNode, useEffect, useCallback } from "react";
import {
  initialAlerts,
  initialChecklist,
  initialCustomer,
  initialOrders,
  initialPaymentMethods,
} from "@/data/seed";
import { Alert, ChecklistTask, CustomerProfile, Order, PaymentMethod, Product, ProductTag, Category, Purchase } from "@/types";
import { supabase } from "@/integrations/supabase/client";
import { getDeviceId } from "@/lib/deviceId";

interface NotificationPrefs {
  promos: boolean;
  orders: boolean;
  ops: boolean;
  emailDigest: boolean;
}
interface SecurityPrefs {
  biometric: boolean;
  faceId: boolean;
  hideAmounts: boolean;
  twoFactor: boolean;
}

interface AppState {
  products: Product[];
  orders: Order[];
  alerts: Alert[];
  paymentMethods: PaymentMethod[];
  checklist: ChecklistTask[];
  purchases: Purchase[];
  customer: CustomerProfile;
  notifications: NotificationPrefs;
  security: SecurityPrefs;
  loadingProducts: boolean;

  addProduct: (p: Omit<Product, "id" | "stock" | "minStock"> & { stock?: number; minStock?: number }) => Promise<void>;
  updateProduct: (id: string, patch: Partial<Omit<Product, "id">>) => Promise<void>;
  adjustStock: (id: string, delta: number) => Promise<void>;
  setPaymentDefault: (id: string) => void;
  addPaymentMethod: () => void;
  toggleChecklist: (id: string) => void;
  registerPurchase: (product: Product) => Promise<void>;
  updateCustomer: (c: Partial<CustomerProfile>) => void;
  setNotifications: (n: NotificationPrefs) => void;
  setSecurity: (s: SecurityPrefs) => void;
}

const AppContext = createContext<AppState | undefined>(undefined);

type DbProduct = {
  id: string;
  name: string;
  category: string;
  price: number;
  door: string | null;
  qr_code: string;
  barcode: string | null;
  tag: string;
  stock: number;
  min_stock: number;
};

type DbPurchase = {
  id: string;
  device_id: string;
  product_id: string | null;
  product_name: string;
  amount: number;
  door: string;
  created_at: string;
};

const fromDbProduct = (r: DbProduct): Product => ({
  id: r.id,
  name: r.name,
  category: r.category as Category,
  price: Number(r.price),
  door: r.door ?? "",
  qrCode: r.qr_code,
  barcode: r.barcode ?? undefined,
  tag: r.tag as ProductTag,
  stock: r.stock,
  minStock: r.min_stock,
});

const fromDbPurchase = (r: DbPurchase): Purchase => {
  const d = new Date(r.created_at);
  const today = new Date();
  const isToday = d.toDateString() === today.toDateString();
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  const isYesterday = d.toDateString() === yesterday.toDateString();
  const time = `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
  const date = isToday
    ? `Hoje · ${time}`
    : isYesterday
      ? `Ontem · ${time}`
      : `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")} · ${time}`;
  return {
    id: r.id,
    productName: r.product_name,
    amount: Number(r.amount),
    date,
    door: r.door,
  };
};

export const AppProvider = ({ children }: { children: ReactNode }) => {
  const [products, setProducts] = useState<Product[]>([]);
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [orders, setOrders] = useState<Order[]>(initialOrders);
  const [alerts] = useState<Alert[]>(initialAlerts);
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>(initialPaymentMethods);
  const [checklist, setChecklist] = useState<ChecklistTask[]>(initialChecklist);
  const [purchases, setPurchases] = useState<Purchase[]>([]);
  const [customer, setCustomer] = useState<CustomerProfile>(initialCustomer);
  const [notifications, setNotifications] = useState<NotificationPrefs>({
    promos: true,
    orders: true,
    ops: false,
    emailDigest: true,
  });
  const [security, setSecurity] = useState<SecurityPrefs>({
    biometric: true,
    faceId: false,
    hideAmounts: false,
    twoFactor: true,
  });

  // ---- Products: admins read full table (incl. qr_code), others read safe view ----
  useEffect(() => {
    let mounted = true;

    // Skip data fetching on landing page to avoid blocking critical path / improve LCP.
    // Data will load when the user navigates into the app.
    if (typeof window !== "undefined" && window.location.pathname === "/") {
      setLoadingProducts(false);
      return;
    }

    const loadProducts = async () => {
      const { data: sessionData } = await supabase.auth.getSession();
      const uid = sessionData.session?.user.id;

      let isAdmin = false;
      if (uid) {
        const { data: roleRow } = await supabase
          .from("user_roles")
          .select("role")
          .eq("user_id", uid)
          .eq("role", "admin")
          .maybeSingle();
        isAdmin = !!roleRow;
      }

      if (isAdmin) {
        const { data } = await supabase
          .from("products")
          .select("*")
          .order("created_at", { ascending: true });
        if (mounted && data) setProducts((data as DbProduct[]).map(fromDbProduct));
      } else {
        const { data } = await supabase
          .from("products_public")
          .select("*")
          .order("created_at", { ascending: true });
        if (mounted && data) {
          setProducts(
            (data as Omit<DbProduct, "qr_code">[]).map((r) =>
              fromDbProduct({ ...r, qr_code: "" } as DbProduct)
            )
          );
        }
      }
      if (mounted) setLoadingProducts(false);
    };

    loadProducts();

    const { data: sub } = supabase.auth.onAuthStateChange(() => {
      loadProducts();
    });

    return () => {
      mounted = false;
      sub.subscription.unsubscribe();
    };
  }, []);

  // ---- Realtime: purchases for current authenticated user ----
  useEffect(() => {
    let mounted = true;
    let channel: ReturnType<typeof supabase.channel> | null = null;

    // Skip on landing page.
    if (typeof window !== "undefined" && window.location.pathname === "/") {
      return;
    }

    const load = async (userId: string) => {
      if (channel) return;
      // Reserve the channel slot synchronously to prevent concurrent load() calls
      // (e.g. from getSession + onAuthStateChange) from creating duplicate subscriptions.
      const ch = supabase.channel(`purchases:${userId}`);
      channel = ch;
      ch.on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "purchases", filter: `user_id=eq.${userId}` },
        (payload) => {
          const row = fromDbPurchase(payload.new as DbPurchase);
          setPurchases((prev) => (prev.find((x) => x.id === row.id) ? prev : [row, ...prev]));
        }
      );
      ch.subscribe();

      const { data } = await supabase
        .from("purchases")
        .select("*")
        .eq("user_id", userId)
        .order("created_at", { ascending: false });
      if (mounted && data) setPurchases((data as DbPurchase[]).map(fromDbPurchase));
    };

    supabase.auth.getSession().then(({ data }) => {
      const uid = data.session?.user.id;
      if (uid) load(uid);
    });

    const { data: sub } = supabase.auth.onAuthStateChange((_event, newSession) => {
      const uid = newSession?.user.id;
      if (uid && !channel) load(uid);
      if (!uid && channel) {
        supabase.removeChannel(channel);
        channel = null;
        setPurchases([]);
      }
    });

    return () => {
      mounted = false;
      sub.subscription.unsubscribe();
      if (channel) supabase.removeChannel(channel);
    };
  }, []);

  const addProduct: AppState["addProduct"] = async (p) => {
    await supabase.from("products").insert({
      name: p.name,
      category: p.category,
      price: p.price,
      door: p.door,
      qr_code: p.qrCode,
      tag: p.tag,
      stock: p.stock ?? 10,
      min_stock: p.minStock ?? 4,
    });
  };

  const updateProduct: AppState["updateProduct"] = async (id, patch) => {
    const dbPatch: {
      name?: string; category?: string; price?: number; door?: string;
      qr_code?: string; tag?: string; stock?: number; min_stock?: number;
    } = {};
    if (patch.name !== undefined) dbPatch.name = patch.name;
    if (patch.category !== undefined) dbPatch.category = patch.category;
    if (patch.price !== undefined) dbPatch.price = patch.price;
    if (patch.door !== undefined) dbPatch.door = patch.door;
    if (patch.qrCode !== undefined) dbPatch.qr_code = patch.qrCode;
    if (patch.tag !== undefined) dbPatch.tag = patch.tag;
    if (patch.stock !== undefined) dbPatch.stock = patch.stock;
    if (patch.minStock !== undefined) dbPatch.min_stock = patch.minStock;
    await supabase.from("products").update(dbPatch).eq("id", id);
  };

  const adjustStock = useCallback(async (id: string, delta: number) => {
    const current = products.find((p) => p.id === id);
    if (!current) return;
    const newStock = Math.max(0, current.stock + delta);
    await supabase.from("products").update({ stock: newStock }).eq("id", id);
  }, [products]);

  const setPaymentDefault = (id: string) => {
    setPaymentMethods((prev) => prev.map((m) => ({ ...m, isDefault: m.id === id })));
  };

  const addPaymentMethod = () => {
    const id = `pm${Date.now()}`;
    setPaymentMethods((prev) => [
      ...prev,
      { id, type: "Cartão", label: `Cartão •••• ${Math.floor(1000 + Math.random() * 8999)}`, isDefault: false },
    ]);
  };

  const toggleChecklist = (id: string) =>
    setChecklist((prev) => prev.map((t) => (t.id === id ? { ...t, done: !t.done } : t)));

  const registerPurchase = async (product: Product) => {
    const { data: sessionData } = await supabase.auth.getSession();
    const userId = sessionData.session?.user.id;
    if (!userId) return;
    const deviceId = getDeviceId();
    await supabase.from("purchases").insert({
      device_id: deviceId,
      user_id: userId,
      product_id: product.id,
      product_name: product.name,
      amount: product.price,
      door: product.door,
    });
    // local order list (not persisted)
    const newOrder: Order = {
      id: `#${String(orders.length + 1).padStart(3, "0")}`,
      customer: customer.name.split(" ")[0],
      productName: product.name,
      amount: product.price,
      status: "Pago",
      date: "Agora",
    };
    setOrders((prev) => [newOrder, ...prev]);
    await adjustStock(product.id, -1);
  };

  const updateCustomer = (c: Partial<CustomerProfile>) =>
    setCustomer((prev) => ({ ...prev, ...c }));

  return (
    <AppContext.Provider
      value={{
        products,
        orders,
        alerts,
        paymentMethods,
        checklist,
        purchases,
        customer,
        notifications,
        security,
        loadingProducts,
        addProduct,
        updateProduct,
        adjustStock,
        setPaymentDefault,
        addPaymentMethod,
        toggleChecklist,
        registerPurchase,
        updateCustomer,
        setNotifications,
        setSecurity,
      }}
    >
      {children}
    </AppContext.Provider>
  );
};

export const useApp = () => {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error("useApp must be used within AppProvider");
  return ctx;
};

export const formatCurrency = (n: number) =>
  n.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
